import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { LOGO } from "@/data/content";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "HOME", id: "home" },
  { label: "SERVICES", id: "services" },
  { label: "ABOUT", id: "about" },
  { label: "GALLERY", id: "gallery" },
  { label: "CONTACT", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ink/85 backdrop-blur-md border-b border-white/5 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("home")}
          className="flex items-center gap-3 group"
          aria-label="MOTO LUXE home"
        >
          <img
            src={LOGO}
            alt="MOTO LUXE"
            className={`w-auto transition-all duration-500 ${
              scrolled ? "h-16" : "h-24"
            } group-hover:opacity-90`}
          />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="relative font-sans text-[13px] tracking-[0.18em] text-cream/70 hover:text-cream transition-colors duration-300 group"
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-now-button"
            onClick={() => go("contact")}
            className="hidden sm:inline-flex items-center border border-gold/70 text-gold hover:bg-gold hover:text-ink transition-all duration-400 px-5 py-2.5 font-mono text-[11px] tracking-[0.22em] uppercase"
          >
            Book Now
          </button>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden text-cream p-1"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-96 border-t border-white/5" : "max-h-0"
        }`}
      >
        <div className="bg-ink/95 backdrop-blur px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-left font-sans text-sm tracking-[0.2em] text-cream/80"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="mobile-book-now-button"
            onClick={() => go("contact")}
            className="mt-1 inline-flex items-center justify-center bg-gold text-ink px-5 py-3 font-mono text-[11px] tracking-[0.22em] uppercase"
          >
            Book Your Detail
          </button>
        </div>
      </div>
    </header>
  );
}
